import type { ApiActivityLog } from '@kaiizen/kai-pro-api';
import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';

import {
  type ScanUrlParts,
  useNavigateToCloseoutScopePreview,
  useNavigateToPunchlistScopePreview,
  useNavigateToScan,
  useNavigateToScope,
} from '@/Client/State/kaiNavigation';
import { isOptedIntoNewExperience } from './newExperienceLib';

export type TimelineEventKey =
  | 'projectCreatedOrRestarted'
  | 'projectHandedOff'
  | 'projectWorkTaken'
  | 'projectReassigned'
  | 'initialWalkAssigned'
  | 'walkAssignedDispatchedReassigned'
  | 'fieldUserAdded'
  | 'fieldUserRemoved'
  | 'initialWalkCompleted'
  | 'scopeEditRequested'
  | 'scopeApproved'
  | 'progressWalkSubmitted'
  | 'changeOrderCreated'
  | 'changeOrderSubmitted'
  | 'changeOrderApprovedRejected'
  | 'punchlistCreated'
  | 'punchlistTaskUpdated'
  | 'closeoutCreated'
  | 'closeoutSubmitted'
  | 'closeoutApproved'
  | 'managerAdded'
  | 'managerRemoved';

export type ClassifiedEntry = {
  key: TimelineEventKey;
  entry: ApiActivityLog;
  emphasis: string;
  suffix: string;
  reassignmentDetails?: {
    addedUsers: string[];
    removedUsers: string[];
  };
};

const FIELD_USER_REASSIGNMENT_WINDOW_MS = 2 * 60 * 1000;

/** "First L." for a person's full name, or undefined when there is no name to shorten. */
export const getShortPersonLabel = (fullName?: string): string | undefined => {
  const parts = fullName?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (parts.length < 2) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

export const getActorLabel = (entry: ApiActivityLog) => getShortPersonLabel(entry.actorName) ?? 'Unknown actor';

const getAssignmentAction = (entry: ApiActivityLog): 'added' | 'removed' | undefined => {
  const normalizedVerb = entry.verb.toLowerCase();
  if (normalizedVerb === 'added' || normalizedVerb === 'assigned') return 'added';
  if (normalizedVerb === 'removed' || normalizedVerb === 'unassigned') return 'removed';

  const normalizedDetails = entry.details?.toLowerCase() ?? '';
  if (normalizedDetails.includes('removed') || normalizedDetails.includes('unassigned')) return 'removed';
  if (normalizedDetails.includes('assigned') || normalizedDetails.includes('added')) return 'added';
};

const getAssignmentPresentation = (entry: ApiActivityLog) => {
  const action = getAssignmentAction(entry);
  if (action === 'added') {
    return {
      key: 'fieldUserAdded' as const,
      emphasis: 'Field user',
      suffix: 'added',
    };
  }
  if (action === 'removed') {
    return {
      key: 'fieldUserRemoved' as const,
      emphasis: 'Field user',
      suffix: 'removed',
    };
  }
  return null;
};

const getProgressSuffix = (entry: ApiActivityLog) => {
  const noun = entry.noun.toLowerCase();
  const normalized = entry.details?.toLowerCase() ?? '';
  if (noun === 'punch list') return { emphasis: 'Punch list', suffix: normalized.includes('resolve') ? 'resolved' : 'updated' };
  if (normalized.includes('punch') && normalized.includes('resolve')) return { emphasis: 'Punch list', suffix: 'resolved' };
  if (normalized.includes('punch')) return { emphasis: 'Punch list', suffix: 'updated' };
  return { emphasis: 'Progress', suffix: 'updated' };
};

export const getAboutUserLabel = (key: TimelineEventKey) => {
  switch (key) {
    case 'managerAdded':
    case 'managerRemoved':
      return 'User';
    case 'fieldUserAdded':
    case 'fieldUserRemoved':
    case 'initialWalkAssigned':
      return 'Field user';
    case 'projectHandedOff':
      return 'Waiting on';
    default:
      return undefined;
  }
};

export const classifyEntry = (entry: ApiActivityLog): ClassifiedEntry | null => {
  const noun = entry.noun.toLowerCase();

  switch (entry.eventType) {
    case 'projectCreated':
      return { key: 'projectCreatedOrRestarted', entry, emphasis: 'Project', suffix: 'created' };
    case 'projectHandedOff':
      return { key: 'projectHandedOff', entry, emphasis: 'Project', suffix: 'handed off' };
    case 'projectWorkTaken':
      return { key: 'projectWorkTaken', entry, emphasis: 'Project', suffix: 'work taken' };
    case 'assignmentChanged': {
      const assignmentPresentation = getAssignmentPresentation(entry);
      if (!assignmentPresentation) return null;
      return { key: assignmentPresentation.key, entry, emphasis: assignmentPresentation.emphasis, suffix: assignmentPresentation.suffix };
    }
    case 'walkCreated':
      if (noun === 'closeout') return { key: 'closeoutCreated', entry, emphasis: 'Closeout', suffix: 'draft created' };
      if (noun === 'scope') return { key: 'initialWalkCompleted', entry, emphasis: 'Scope', suffix: 'created' };
      return null;
    case 'walkCompleted':
      if (noun === 'closeout') return { key: 'closeoutCreated', entry, emphasis: 'Closeout', suffix: 'created' };
      return null;
    case 'scopeSubmitted':
      if (noun === 'scope') return { key: 'initialWalkCompleted', entry, emphasis: 'Scope', suffix: 'submitted' };
      if (noun === 'progress') return { key: 'progressWalkSubmitted', entry, emphasis: 'Progress', suffix: 'updated' };
      if (noun === 'closeout' || noun === 'close out') return { key: 'closeoutSubmitted', entry, emphasis: 'Closeout', suffix: 'submitted' };
      return null;
    case 'scopeRejected':
      if (noun === 'scope') return { key: 'scopeEditRequested', entry, emphasis: 'Scope', suffix: 'addendum submitted' };
      return null;
    case 'scopeApproved':
      if (noun === 'scope') return { key: 'scopeApproved', entry, emphasis: 'Scope', suffix: 'approved' };
      if (noun === 'closeout' || noun === 'close out') return { key: 'closeoutApproved', entry, emphasis: 'Closeout', suffix: 'approved' };
      return null;
    case 'changeOrderCreated':
      return { key: 'changeOrderCreated', entry, emphasis: 'Change order', suffix: 'created' };
    case 'changeOrderUpdated':
      if (entry.verb !== 'submitted') return null;
      return { key: 'changeOrderSubmitted', entry, emphasis: 'Change order', suffix: 'submitted' };
    case 'changeOrderApproved':
    case 'changeOrderRejected':
      return {
        key: 'changeOrderApprovedRejected',
        entry,
        emphasis: 'Change order',
        suffix: entry.eventType === 'changeOrderApproved' ? 'approved' : 'rejected',
      };
    case 'walkUpdated':
      if (noun === 'scope' && entry.verb === 'dispatched') {
        return { key: 'walkAssignedDispatchedReassigned', entry, emphasis: 'Initial walk', suffix: 'dispatched' };
      }
      if (noun === 'progress' || noun === 'punch list') {
        const progressPresentation = getProgressSuffix(entry);
        return { key: 'punchlistTaskUpdated', entry, emphasis: progressPresentation.emphasis, suffix: progressPresentation.suffix };
      }
      return null;
    case 'punchlistCreated':
      return { key: 'punchlistCreated', entry, emphasis: 'Punch list', suffix: 'created' };
    case 'managerAdded':
      return { key: 'managerAdded', entry, emphasis: 'Manager', suffix: 'added' };
    case 'managerRemoved':
      return { key: 'managerRemoved', entry, emphasis: 'Manager', suffix: 'removed' };
    default:
      return null;
  }
};

/**
 * Where a change-order entry's link lands. The artifact panel is only routed for readers
 * opted into the new experience; everyone else would fall through to the 404 page, so they
 * get the change-order page, which both experiences serve.
 */
export const getChangeOrderNavigation = ({ scanId, newExperience }: { scanId: string; newExperience: boolean }): ScanUrlParts =>
  newExperience
    ? { scanId, focusPanel: 'artifact', artifactScanId: scanId, artifactDocument: 'changeOrder' }
    : { scanId, page: 'changeOrder' };

export const useScanNavLink = () => {
  const { featureFlags } = useFeatureFlags();
  const newExperience = isOptedIntoNewExperience(featureFlags);
  const navigateToScope = useNavigateToScope();
  const navigateToScan = useNavigateToScan();
  const navigateToCloseout = useNavigateToCloseoutScopePreview();
  const navigateToPunchlist = useNavigateToPunchlistScopePreview();

  return (key: TimelineEventKey, scanId?: string) => {
    if (!scanId) return null;
    switch (key) {
      case 'initialWalkCompleted':
      case 'scopeEditRequested':
      case 'scopeApproved':
        return { label: 'View Scope', onClick: () => navigateToScope({ scanId }) };
      case 'changeOrderCreated':
      case 'changeOrderSubmitted':
      case 'changeOrderApprovedRejected':
        return {
          label: 'View Change Order',
          onClick: () => navigateToScan(getChangeOrderNavigation({ scanId, newExperience })),
        };
      case 'closeoutCreated':
      case 'closeoutSubmitted':
      case 'closeoutApproved':
        return { label: 'Review Closeout', onClick: () => navigateToCloseout({ scanId }) };
      case 'punchlistCreated':
      case 'punchlistTaskUpdated':
        return { label: 'View progress', onClick: () => navigateToPunchlist({ scanId }) };
      default:
        return null;
    }
  };
};

const isFieldUserAssignmentEntry = (classified: ClassifiedEntry) => classified.key === 'fieldUserAdded' || classified.key === 'fieldUserRemoved';

const getEntryTimestamp = (value: Date | string | undefined) => {
  const timestamp = value ? new Date(value).getTime() : 0;
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getAssignmentGroupingActor = (entry: ApiActivityLog) => entry.actorUserId ?? entry.actorName ?? '';

const shouldGroupFieldUserAssignments = (anchor: ApiActivityLog, candidate: ApiActivityLog) =>
  anchor.scanId === candidate.scanId
  && getAssignmentGroupingActor(anchor) === getAssignmentGroupingActor(candidate)
  && Math.abs(getEntryTimestamp(anchor.createdAt) - getEntryTimestamp(candidate.createdAt)) <= FIELD_USER_REASSIGNMENT_WINDOW_MS;

const getAssignmentUserLabel = (entry: ApiActivityLog) => entry.aboutUserName ?? entry.aboutUserId ?? 'Unknown field user';

const isInitialAssignmentContext = (assignmentEntry: ClassifiedEntry, entries: ClassifiedEntry[]) =>
  entries.some(candidate =>
    (candidate.key === 'projectCreatedOrRestarted' || candidate.key === 'initialWalkCompleted')
    && candidate.entry.projectId === assignmentEntry.entry.projectId
    && candidate.entry.scanId === assignmentEntry.entry.scanId
    && Math.abs(getEntryTimestamp(candidate.entry.createdAt) - getEntryTimestamp(assignmentEntry.entry.createdAt)) <= FIELD_USER_REASSIGNMENT_WINDOW_MS
  );

const getUniqueAssignmentUserLabels = (entries: ClassifiedEntry[], key: 'fieldUserAdded' | 'fieldUserRemoved') => [
  ...new Set(
    entries
      .filter(entry => entry.key === key)
      .map(entry => getAssignmentUserLabel(entry.entry)),
  ),
];

export const combineProjectReassignments = (entries: ClassifiedEntry[]): ClassifiedEntry[] => {
  const result: ClassifiedEntry[] = [];

  for (let index = 0; index < entries.length; index++) {
    const current = entries[index];
    if (!current || !isFieldUserAssignmentEntry(current)) {
      if (current) result.push(current);
      continue;
    }

    const assignmentBatch = [current];
    let nextIndex = index + 1;
    while (nextIndex < entries.length) {
      const candidate = entries[nextIndex];
      if (!candidate || !isFieldUserAssignmentEntry(candidate)) break;
      if (!shouldGroupFieldUserAssignments(current.entry, candidate.entry)) break;
      assignmentBatch.push(candidate);
      nextIndex++;
    }

    const addedUsers = getUniqueAssignmentUserLabels(assignmentBatch, 'fieldUserAdded');
    const removedUsers = getUniqueAssignmentUserLabels(assignmentBatch, 'fieldUserRemoved');

    if (addedUsers.length && removedUsers.length) {
      result.push({
        ...current,
        key: 'projectReassigned',
        emphasis: 'Project',
        suffix: 'reassigned',
        reassignmentDetails: { addedUsers, removedUsers },
      });
    } else if (
      assignmentBatch.length === 1
      && current.key === 'fieldUserAdded'
      && isInitialAssignmentContext(current, entries)
    ) {
      result.push({ ...current, key: 'initialWalkAssigned', emphasis: 'Initial walk', suffix: 'assigned' });
    } else {
      result.push(...assignmentBatch);
    }

    index = nextIndex - 1;
  }

  return result;
};

const getCollapseGroupKey = (classified: ClassifiedEntry): string => {
  const { key, entry } = classified;
  if (key === 'projectReassigned') return `${key}::${entry.id}`;
  const subject = entry.aboutUserId ?? entry.scanId ?? '';
  return `${key}::${subject}`;
};

export const collapseConsecutiveSameKind = (entries: ClassifiedEntry[]): ClassifiedEntry[] => {
  const result: ClassifiedEntry[] = [];
  let lastGroupKey: string | null = null;
  for (const classified of entries) {
    const groupKey = getCollapseGroupKey(classified);
    if (groupKey === lastGroupKey) continue;
    result.push(classified);
    lastGroupKey = groupKey;
  }
  return result;
};
